/* eslint-disable typescript-enum/no-enum */

import type {
  ICameraVideoTrack,
  ILocalVideoTrack,
  IMicrophoneAudioTrack,
  UID,
} from 'agora-rtc-sdk-ng';
import type { QueryResponse, QueryRequest } from 'dreamstate';

import type { Maybe, Optional } from '@vimeo/core/types/generic';

import type { IAgoraRemoteMediaDescriptor, TAgoraSDK } from '../types/agora';
import type { EEventConnectionType } from '../types/broadcaster';
import type { EIngestStatus } from '../types/event';
import type { IFirebaseDependencies, TFirebaseApp } from '../types/firebase';
import type {
  EGraphicsDescriptorType,
  IBroadcasterInfo,
  IGraphic,
  IGraphicsCache,
  IGraphicsScene,
} from '../types/graphics';
import type { IGuestSeat } from '../types/guest';
import type { IInteractionApplicationUser } from '../types/interaction_room';
import type { IFirebaseInteractionCredentials } from '../types/interaction_tools';
import type { IMediaVideoConfig } from '../types/media';

export const ELiveQuery = {
  CAN_STREAM: 'CAN_STREAM',
  INGEST_STATUS: 'INGEST_STATUS',
  LIVE_COMPOSER_STATUSES: 'LIVE_COMPOSER_STATUSES',
  IS_BOKEH: 'IS_BOKEH',
} as const;

export enum EDependencyQuery {
  FIREBASE_DEPENDENCIES = 'FIREBASE_LAZY_DEPENDENCIES',
  AGORA_DEPENDENCIES = 'AGORA_LAZY_DEPENDENCIES',
}

export type TAgoraDependenciesQueryResponse = QueryResponse<TAgoraSDK>;

export type TFirebaseDependenciesQueryResponse = QueryResponse<IFirebaseDependencies>;

export enum ELiveGraphicsQuery {
  BROADCASTER_INFO = 'BROADCASTER_INFO',
  GRAPHICS_ITEM = 'GRAPHICS_ITEM',
  GUEST_ITEM = 'GUEST_ITEM',
  COMPOSER_CACHE_STATE = 'COMPOSER_CACHE_STATE',
  CREATE_TEMPLATE_GRAPHICS = 'CREATE_TEMPLATE_GRAPHICS',
  REMOVE_ALL_SCENES = 'REMOVE_ALL_SCENES',
  CREATE_SCENES_FROM_PARTIAL = 'CREATE_SCENES_FROM_PARTIAL',
}

export type TBroadcasterInfoQueryResponse = QueryResponse<IBroadcasterInfo>;

export type TCreateScenesQueryResponse = QueryResponse<Array<IGraphicsScene>>;

export type TCreateScenesQueryRequest = QueryRequest<{
  scenes: Array<Partial<IGraphicsScene>>;
  index: number;
}>;

export type TGraphicsItemQueryRequest = QueryRequest<{ type: EGraphicsDescriptorType; id: string }>;

export type TCreateTemplateGraphicsQueryResponse = QueryRequest<Array<IGraphic>>;

export type TGraphicsItemQueryResponse = QueryResponse<Optional<IGraphic>>;

export type TGuestItemQueryRequest = QueryRequest<{ id: string }>;

export type TGuestItemQueryResult = QueryResponse<Optional<IGuestSeat>>;

export type TIsMediaOnSceneQueryRequest = QueryRequest<UID>;

export type TIsMediaOnSceneQueryResponse = QueryRequest<boolean>;

export type TComposerCacheStateQueryResponse = QueryResponse<IGraphicsCache>;

export enum ELiveMediaQuery {
  CURRENT_LOCAL_MEDIA = 'CURRENT_LOCAL_MEDIA',
  CURRENT_LOCAL_VIDEO_CONFIG = 'CURRENT_LOCAL_VIDEO_CONFIG',
  CURRENT_REMOTE_MEDIA = 'CURRENT_REMOTE_MEDIA',
  CURRENT_PLAYBACK_DEVICE = 'CURRENT_PLAYBACK_DEVICE',
  IS_REMOTE_MEDIA_PLAYING = 'IS_REMOTE_MEDIA_PLAYING',
}

export type TCurrentRemoteMediaQueryResponse = QueryResponse<Array<IAgoraRemoteMediaDescriptor>>;

export type TCurrentLocalVideoConfigQueryResponse = QueryResponse<
  IMediaVideoConfig<ICameraVideoTrack>
>;

export type TCurrentPlaybackDeviceQueryResponse = QueryResponse<{
  playbackDeviceId: string;
  playbackDevices: Array<MediaDeviceInfo>;
}>;

export type TCurrentLocalMediaQueryResponse = QueryResponse<{
  video: Optional<ICameraVideoTrack>;
  audio: Optional<IMicrophoneAudioTrack>;
  screen: Optional<ILocalVideoTrack>;
}>;

export enum ELiveConnectionQuery {
  IS_REMOTE_ALLOWED_IN_ROOM = 'IS_REMOTE_ALLOWED_IN_ROOM',
  IS_EVENT_BROADCASTER_CONNECTION = 'IS_EVENT_BROADCASTER_CONNECTION',
  IS_SCREEN_SHARING_ALLOWED = 'IS_SCREEN_SHARING_ALLOWED',
  CURRENT_CONNECTION_UID = 'CURRENT_CONNECTION_UID',
  CURRENT_EVENT_CONNECTION_TYPE = 'CURRENT_EVENT_CONNECTION_TYPE',
  CURRENT_CONNECTION_MEDIA_UID = 'CURRENT_CONNECTION_MEDIA_UID',
  CURRENT_CONNECTION_SCREEN_UID = 'CURRENT_CONNECTION_SCREEN_UID',
  FIREBASE_COMPOSER_APPLICATION = 'FIREBASE_COMPOSER_APPLICATION',
  FIREBASE_DEPENDENCIES = 'FIREBASE_DEPENDENCIES',
  FIREBASE_INTERACTION_CREDENTIALS = 'FIREBASE_INTERACTION_CREDENTIALS',
  COMPOSER_FIREBASE_CONNECTION_STATE_READY = 'COMPOSER_FIREBASE_CONNECTION_STATE_READY',
  IS_SCHEDULED_CLOUD_SESSION = 'IS_SCHEDULED_CLOUD_SESSION',
}

export type TFirebaseApplicationQueryResponse = QueryResponse<Optional<TFirebaseApp>>;

export type TIsRemoteAllowedInRoomQueryResponse = QueryResponse<boolean>;

export type TIsScreenSharingAllowedQueryResponse = QueryResponse<boolean>;

export type TISEventBroadcasterConnectionQueryResponse = QueryResponse<boolean>;

export type TIsMediaPlayingQueryResponse = QueryResponse<boolean>;

export type TIngestStatusQueryResponse = QueryResponse<EIngestStatus>;

export type TFirebaseConnectionStateReadyQueryResponse = QueryResponse<boolean>;

export type TCurrentConnectionAgoraIdQueryResponse = QueryResponse<UID>;

export type TCurrentConnectionAgoraMediaIdQueryResponse = QueryResponse<UID>;

export type TCurrentConnectionAgoraScreenIdQueryResponse = QueryResponse<UID>;

export type TCurrentEventConnectionTypeQueryResponse = QueryResponse<EEventConnectionType>;

export type TFirebaseInteractionCredentialsQueryResponse = QueryResponse<
  Maybe<IFirebaseInteractionCredentials>
>;

export enum ELiveInteractionQuery {
  LIVE_INTERACTION_USER = 'LIVE_INTERACTION_USER',
}

export type TLiveInteractionUserQueryResponse = QueryResponse<
  Optional<IInteractionApplicationUser>
>;

export enum EGuestQuery {
  SESSION_JOINED = 'SESSION_JOINED',
}

export type TSessionJoinedQueryResponse = QueryResponse<boolean>;
